interface Contact {
	name: string;
	tel: string;
	email: string;
	title: string;
	areas: string[];
	region?: string;
	hiddenInMap?: boolean;
}

interface VertriebData {
	contacts: Contact[];
	areas: Record<
		string,
		{
			name: string;
			region: string;
			PLZs: string[];
		}
	>;
}

declare global {
	interface Window {
		vertriebData: VertriebData;
	}
}

function initVertrieb() {
	// Area Bereiche mit PLZ Range
	const areas: Record<string, { name: string; region: string; PLZs: string[] }> = {
		vg_n: { name: 'Vertrieb Nord', region: 'north', PLZs: ['16000-29999'] },
		vg_berlin: { name: 'Vertrieb Berlin', region: 'north', PLZs: ['10000-13999'] },
		vg_mn: {
			name: 'Vertrieb Mitte-Nord',
			region: 'north',
			PLZs: ['06100-06699', '30000-33999', '37000-39999', '48000-48999', '49000-49999', '99000-99999'],
		},
		vg_o: {
			name: 'Vertrieb Ost',
			region: 'north',
			PLZs: ['01000-05999', '07000-07999', '06700-06999', '08000-08999', '09000-09999', '14000-14999', '15000-15999'],
		},
		vg_w: { name: 'Vertrieb West', region: 'south', PLZs: ['34000-36999', '40000-47999', '50000-59999'] },
		vg_m: { name: 'Vertrieb Mitte', region: 'south', PLZs: ['55000-55999', '60000-69999'] },
		vg_ms: { name: 'Vertrieb Mitte-Süd', region: 'south', PLZs: ['90000-98999'] },
		vg_so: { name: 'Vertrieb Süd-Ost', region: 'south', PLZs: ['80000-89999'] },
		vg_s: {
			name: 'Vertrieb Süd',
			region: 'south',
			PLZs: ['70000-70999', '71000-71999', '73000-73999', '74000-74999', '75300-75399', '75400-75499', '78500-78799'],
		},
		vg_sw: {
			name: 'Vertrieb Süd-West',
			region: 'south',
			PLZs: [
				'75100-75199',
				'75200-75299',
				'75500-75999',
				'76000-76999',
				'77000-77999',
				'78100-78499',
				'78800-78899',
				'78900-78999',
				'79000-79999',
				'72000-72999',
			],
		},
		general: { name: 'Allgemein', region: 'general', PLZs: [] },
	};

	const contacts: Contact[] = Array.isArray(window.vertriebData?.contacts) ? window.vertriebData.contacts : [];

	const output = document.querySelector('.vertrieb__search-result');
	const showMoreBtn = document.querySelector('.action-button') as HTMLButtonElement | null;
	const plzInput = document.querySelector('.vertrieb__search-plz input') as HTMLInputElement | null;
	const dropdown = document.querySelector('.area-select-dropdown') as HTMLSelectElement | null;
	const plzSearchIcon = document.querySelector('.vertrieb__search-plz svg') as SVGElement | null;

	const areaRegions: Record<string, string> = {
		vg_n: 'Borkheide',
		vg_berlin: 'Borkheide',
		vg_mn: 'Borkheide',
		vg_o: 'Borkheide',
		vg_w: 'Waiblingen',
		vg_m: 'Waiblingen',
		vg_ms: 'Waiblingen',
		vg_so: 'Waiblingen',
		vg_s: 'Waiblingen',
		vg_sw: 'Waiblingen',
		general: 'Allgemein',
	};

	const isEnglish = document.documentElement.lang === 'en';

	const VERTRIEB_MESSAGES_DE = {
		location: 'Standort',
		invalidPlz: 'Bitte 5-stellige PLZ eingeben',
		noAreaFound: 'Kein Gebiet gefunden',
		showLess: 'Weniger anzeigen',
		showMore: 'Mehr anzeigen',
		generalArea: 'Bereich Allgemein',
	};

	const VERTRIEB_MESSAGES_EN = {
		location: 'Location',
		invalidPlz: 'Please enter a 5-digit postal code',
		noAreaFound: 'No area found',
		showLess: 'Show less',
		showMore: 'Show more',
		generalArea: 'General area',
	};

	const VERTRIEB_MESSAGES = isEnglish ? VERTRIEB_MESSAGES_EN : VERTRIEB_MESSAGES_DE;

	function getAreaByPLZ(plz: string): string | null {
		const plzClean = plz.replace(/\D/g, '');
		if (plzClean.length !== 5) return null; // nur 5-stellige PLZ

		for (const [areaId, data] of Object.entries(areas)) {
			if (!Array.isArray(data.PLZs)) continue;

			for (const rule of data.PLZs) {
				if (rule.includes('-')) {
					const [start, end] = rule.split('-');
					if (plzClean >= start && plzClean <= end) return areaId;
				} else {
					if (plzClean === rule) return areaId;
				}
			}
		}

		return null;
	}

	function triggerPlzSearch() {
		if (!plzInput || !output) return;

		const value = plzInput.value.trim();

		// Prüfen auf 5-stellige Zahl
		if (!/^\d{5}$/.test(value)) {
			output.innerHTML = `
			<p class="vertrieb__search-error">${VERTRIEB_MESSAGES.invalidPlz}</p>`;

			// Mehr/ Weniger Button deaktivieren
			if (showMoreBtn) {
				showMoreBtn.style.display = 'none';
				showMoreBtn.disabled = true;
			}

			highlightArea(null);
			return;
		}

		const areaId = getAreaByPLZ(value);

		if (!areaId) {
			output.innerHTML = `<p>${VERTRIEB_MESSAGES.noAreaFound}</p>`;

			// Mehr/Weniger Button deaktivieren
			if (showMoreBtn) {
				showMoreBtn.style.display = 'none';
				showMoreBtn.disabled = true;
			}

			highlightArea(null);
			return;
		}

		// Gebiet gefunden
		if (showMoreBtn) {
			showMoreBtn.style.display = 'none';
			showMoreBtn.disabled = true;
		}

		renderContacts(filterContactsByArea(areaId), true);
		highlightArea(areaId);

		if (dropdown) dropdown.value = areaId;
	}

	function filterContactsByArea(areaId: string | null) {
		if (!areaId) return [];
		return contacts.filter((c) => Array.isArray(c.areas) && c.areas.includes(areaId));
	}

	function renderContactCard(contact: Contact) {
		return `
        <div class="contact__card">
            <strong class="contact__card-position -size-sm">${contact.title}</strong>
            <p class="contact__card-name -size-sm">${contact.name}</p>
            <div class="contact__card-mobile">
                <a class="-size-sm" href="tel:${contact.tel}">${contact.tel}</a>
                <a class="contact__card-mail -size-sm" href="mailto:${contact.email}">${contact.email}</a>
            </div>
        </div>
        `;
	}

	function renderContacts(filteredContacts: Contact[], groupedByRegion = false) {
		if (!output) return;

		let html = '';
		const generalContacts = contacts.filter((c) => c.areas.includes('general'));

		if (groupedByRegion) {
			const regions: Record<string, Contact[]> = { Borkheide: [], Waiblingen: [], Allgemein: [] };
			filteredContacts.forEach((contact) => {
				let region = 'Allgemein';
				for (const area of contact.areas) {
					if (areaRegions[area]) {
						region = areaRegions[area];
						break;
					}
				}
				regions[region].push(contact);
			});

			generalContacts.forEach((contact) => {
				if (!regions['Allgemein'].includes(contact)) regions['Allgemein'].push(contact);
			});

			['Borkheide', 'Waiblingen', 'Allgemein'].forEach((regionName) => {
				if (regions[regionName].length > 0) {
					html += `<h4 class="-size-lg">${VERTRIEB_MESSAGES.location} ${regionName}</h4>`;
					regions[regionName].forEach((contact) => (html += renderContactCard(contact)));
					html += '<hr class="contact__card-line">';
				}
			});
		} else {
			if (filteredContacts.length > 0) {
				const firstAreaId = filteredContacts[0].areas.find((a) => a !== 'general');
				const regionName = firstAreaId ? areaRegions[firstAreaId] : '';
				html += `<h4 class="-size-lg">${VERTRIEB_MESSAGES.location} ${regionName}</h4>`;
				filteredContacts.forEach((contact) => (html += renderContactCard(contact)));
			}

			if (generalContacts.length > 0) {
				html += `<h4 class="-size-lg">${VERTRIEB_MESSAGES.generalArea}</h4>`;
				generalContacts.forEach((contact) => {
					if (!filteredContacts.includes(contact)) html += renderContactCard(contact);
				});
			}
		}

		output.innerHTML = html;
	}

	function highlightArea(areaId: string | null) {
		document.querySelectorAll('.highlight').forEach((el) => el.classList.remove('highlight'));

		if (!areaId || areaId === 'general') {
			// general highlighted nichts
			console.log('[DEBUG] Keine Area zum Highlighten (general wird ignoriert)');
			return;
		}

		const group = document.querySelector(`.trigger--${areaId}`);
		if (!group) {
			console.error('[DEBUG] SVG-Gruppe nicht gefunden für Area:', areaId);
			return;
		}

		group.classList.add('highlight');
		group.querySelectorAll('path, polygon, circle, rect, polyline').forEach((el) => el.classList.add('highlight'));
	}

	// Events
	document.querySelectorAll<SVGGElement>('.vertriebs-map__group[class*="trigger--"]').forEach((group) => {
		group.addEventListener('click', () => {
			const areaClass = Array.from(group.classList).find((c) => c.startsWith('trigger--'));
			if (!areaClass) return;
			const areaId = areaClass.replace('trigger--', '');

			// Mehr/Less Button deaktivieren
			if (showMoreBtn) {
				showMoreBtn.style.display = 'none';
				showMoreBtn.disabled = true;
			}

			highlightArea(areaId);
			renderContacts(filterContactsByArea(areaId), true);
			if (dropdown) dropdown.value = areaId;
			if (plzInput) plzInput.value = '';
		});
	});

	plzInput?.addEventListener('keydown', (e) => {
		if (e.key !== 'Enter') return;

		e.preventDefault();
		triggerPlzSearch();
	});

	plzSearchIcon?.addEventListener('click', () => {
		triggerPlzSearch();
	});

	plzSearchIcon?.addEventListener('touchend', (e) => {
		e.preventDefault();
		triggerPlzSearch();
	});

	dropdown?.addEventListener('change', () => {
		const areaId = dropdown.value || null;

		// Mehr/ Weniger Button deaktivieren
		if (showMoreBtn) {
			showMoreBtn.style.display = 'none';
			showMoreBtn.disabled = true;
		}

		renderContacts(areaId ? filterContactsByArea(areaId) : contacts, true);
		highlightArea(areaId);
	});

	if (showMoreBtn) {
		showMoreBtn.style.display = 'inline-block';
		showMoreBtn.disabled = false;
		showMoreBtn.textContent = VERTRIEB_MESSAGES.showMore;
	}

	showMoreBtn?.addEventListener('click', () => {
		if (!output) return;
		output.classList.toggle('expanded');
		showMoreBtn.textContent = output.classList.contains('expanded')
			? `${VERTRIEB_MESSAGES.showLess}`
			: `${VERTRIEB_MESSAGES.showMore}`;
	});

	// Default: pro Area nur den ersten Kontakt anzeigen
	const firstPerArea: Record<string, Contact> = {};

	contacts.forEach((contact) => {
		if (!Array.isArray(contact.areas)) return;

		contact.areas.forEach((areaId) => {
			if (!firstPerArea[areaId]) {
				firstPerArea[areaId] = contact;
			}
		});
	});

	// Vertriebs-Areas (nicht doppelt)
	const defaultContacts = Object.entries(firstPerArea)
		.filter(([areaId]) => areaId !== 'general')
		.map(([, contact]) => contact);

	if (firstPerArea['general']) {
		defaultContacts.push(firstPerArea['general']);
	}

	if (showMoreBtn) {
		showMoreBtn.style.display = 'inline-block';
		showMoreBtn.disabled = false;
	}

	renderContacts(defaultContacts, true);
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		waitForVertriebData();
	});
} else {
	waitForVertriebData();
}

function waitForVertriebData() {
	if (window.vertriebData) {
		console.log('[DEBUG] vertriebData schon da:', window.vertriebData);
		initVertrieb();
	} else {
		if (window.location.hostname === 'localhost') {
			// Lokaler Dummy vertriebsData -> wird in Redaxo von Pflege gerendert
			window.vertriebData = {
				contacts: [
					{
						name: 'Nord Mustermann',
						tel: '01234 567890',
						email: 'max@example.com',
						title: 'Vertrieb',
						areas: ['vg_n'],
					},
					{
						name: 'Nicht anzeigen',
						tel: '01234 567890',
						email: 'max@example.com',
						title: 'Vertrieb',
						areas: ['vg_n'],
					},
					{
						name: 'Berlin Mustermann',
						tel: '01234 567890',
						email: 'max@example.com',
						title: 'Vertrieb',
						areas: ['vg_berlin'],
					},
					{
						name: 'Mitte Nord Mustermann',
						tel: '01234 567890',
						email: 'max@example.com',
						title: 'Vertrieb',
						areas: ['vg_mn'],
					},
					{
						name: 'ost Mustermann',
						tel: '01234 567890',
						email: 'max@example.com',
						title: 'Vertrieb',
						areas: ['vg_o'],
					},
					{
						name: 'west Mustermann',
						tel: '01234 567890',
						email: 'max@example.com',
						title: 'Vertrieb',
						areas: ['vg_w'],
					},
					{
						name: 'mitte sued Mustermann',
						tel: '01234 567890',
						email: 'max@example.com',
						title: 'Vertrieb',
						areas: ['vg_ms'],
					},
					{
						name: 'mitte Mustermann',
						tel: '01234 567890',
						email: 'max@example.com',
						title: 'Vertrieb',
						areas: ['vg_m'],
					},
					{
						name: 'sued ost Mustermann',
						tel: '01234 567890',
						email: 'max@example.com',
						title: 'Vertrieb',
						areas: ['vg_so'],
					},
					{
						name: 'sued Mustermann',
						tel: '01234 567890',
						email: 'max@example.com',
						title: 'Vertrieb',
						areas: ['vg_s'],
					},
					{
						name: 'sued west Mustermann',
						tel: '01234 567890',
						email: 'max@example.com',
						title: 'Vertrieb',
						areas: ['vg_sw'],
					},
				],
				areas: {
					vg_n: { name: 'Vertrieb Nord', region: 'north', PLZs: ['16-29'] },
				},
			};
			console.log('[DEBUG] Dummy vertriebData gesetzt:', window.vertriebData);
			initVertrieb();
			return;
		}

		const interval = setInterval(() => {
			if (window.vertriebData) {
				clearInterval(interval);
				console.log('[DEBUG] vertriebData jetzt verfügbar:', window.vertriebData);
				initVertrieb();
			} else {
				console.log('[DEBUG] noch nicht verfügbar...');
			}
		}, 50);
	}
}
